import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { authAPI } from "../../../services/apiService";
import { setSessionCookie } from "../../../utils/sessionCookie";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Showcase } from "../../components/auth/Showcase";
import { Stepper } from "../../components/auth/Stepper";
import { FormError } from "../../components/auth/fields";
import { AUTH_PATHS, dialCodeOf, isIndia, stepPath, useSignup } from "../../components/auth/signupState";
import { GhostButton } from "../../components/crm/leadsKit";
import { PrimaryButton } from "../../components/ui";

/**
 * Verify (design: screens/Verify.tsx).
 *
 * The code goes on WhatsApp to the mobile number, or by email when WhatsApp
 * can't deliver it (item 267). The copy names where it actually went, from
 * send-otp's `channel` (data.otpVia), and the resend link can switch channel.
 *
 * Verify → POST /api/auth/verify-otp with the old Register.js body plus
 * organizationType, website and legalName (item 266); it creates the account
 * and organisation and returns token + user, stored the same way as the old
 * page. Resend → POST /api/auth/resend-otp { email, channel }.
 */

const LEN = 6;
const MISMATCH = "That code didn't match. Check the latest message or resend the code.";

export default function Verify() {
  const { data, set } = useSignup();
  const navigate = useNavigate();
  const [code, setCode] = useState(() => Array(LEN).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(30);
  const [resent, setResent] = useState(false);
  const refs = useRef([]);
  // Where the code actually went (send-otp / resend-otp `channel`).
  const onWhatsApp = data.otpVia === "whatsapp";
  const phone = String(data.phone || "").replace(/\D/g, "");

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const put = (i, v) => {
    const digits = v.replace(/\D/g, "");
    if (!digits) {
      const next = [...code];
      next[i] = "";
      setCode(next);
      return;
    }
    const next = [...code];
    digits.split("").slice(0, LEN - i).forEach((d, k) => {
      next[i + k] = d;
    });
    setCode(next);
    setError("");
    refs.current[Math.min(i + digits.length, LEN - 1)]?.focus();
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < LEN - 1) refs.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    e.preventDefault();
    put(0, e.clipboardData.getData("text"));
  };

  const india = isIndia(data);
  const verify = async () => {
    const value = code.join("");
    if (value.length < LEN) {
      setError("Enter all 6 digits");
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.verifyOTP({
        email: data.otpSentTo,
        otp: value,
        organizationName: data.orgName.trim(),
        organizationEmail: "",
        organizationPhone: "",
        // The organisation's dialling code decides the trial outside India (subscriptionService.isOutsideIndia).
        organizationCountryCode: india ? "+91" : dialCodeOf(data.country) || data.dialCode || "",
        address: data.address.trim(),
        city: data.city.trim(),
        state: data.state,
        country: data.country || "India",
        pincode: data.pin,
        gstin: india && data.hasGst ? data.gstin : "",
        pan: india ? data.pan : "",
        taxId: india ? "" : data.taxId.trim(),
        organizationType: data.orgType || "",
        website: data.website ? `https://${data.website.replace(/^https?:\/\//, "")}` : "",
        legalName: data.legalName.trim(),
        hearAbout: "",
        inviteEmails: [],
      });
      if (response.data.success) {
        localStorage.setItem("token", response.data.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.data.user));
        setSessionCookie();
        window.dispatchEvent(new Event("auth:login"));
        set({ verified: true, password: "" });
        navigate(stepPath("setup"), { replace: true });
        return;
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      const message = err.response?.data?.message;
      setError(message === "Invalid OTP" ? MISMATCH : message || "Registration failed. Please try again.");
    }
  };

  const resend = async (channel = data.otpChannel || "whatsapp") => {
    setError("");
    try {
      const response = await authAPI.resendOTP({ email: data.otpSentTo, channel });
      if (response.data.success) {
        set({
          otpChannel: channel,
          otpVia: response.data.channel === "whatsapp" ? "whatsapp" : "email",
          otpWhatsappError: response.data.whatsapp_error || "",
        });
        setSeconds(30);
        setResent(true);
        setCode(Array(LEN).fill(""));
        refs.current[0]?.focus();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP.");
    }
  };

  return (
    <AuthLayout
      topRight={
        <p className="text-small text-muted">
          Have an account?{" "}
          <Link to={AUTH_PATHS.signin} className="font-semibold text-ink underline-offset-4 hover:underline">
            Sign in
          </Link>
        </p>
      }
      aside={<Showcase step="verify" data={data} />}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          verify();
        }}
        noValidate
        className="flex animate-fade-in flex-col gap-8"
      >
        <Stepper current="verify" />
        <div className="flex flex-col gap-2">
          <h1 className="text-h1 font-semibold">{onWhatsApp ? "Check WhatsApp" : "Verify your email"}</h1>
          <p className="text-body text-muted">
            Enter the 6-digit code we sent {onWhatsApp ? "on WhatsApp to " : "to "}
            <span className="font-medium text-ink">{onWhatsApp ? `${data.dialCode || "+91"} ${phone}` : data.otpSentTo || "your work email"}</span>.{" "}
            <Link to={stepPath("basic")} className="font-medium text-sky-deep hover:underline">
              {onWhatsApp ? "Change number" : "Change email"}
            </Link>
          </p>
          {!onWhatsApp && data.otpChannel === "whatsapp" && (
            <p className="text-small text-muted">WhatsApp couldn't deliver the code{data.otpWhatsappError ? ` (${data.otpWhatsappError})` : ""}, so we emailed it instead.</p>
          )}
        </div>

        {error && <FormError>{error}</FormError>}

        <div className="flex flex-col gap-4">
          <div className="flex gap-2 sm:gap-3" role="group" aria-label="Verification code">
            {code.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  refs.current[i] = el;
                }}
                value={d}
                onChange={(e) => put(i, e.target.value)}
                onKeyDown={(e) => onKey(i, e)}
                onPaste={onPaste}
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={LEN}
                aria-label={`Digit ${i + 1}`}
                className={`h-14 w-full min-w-0 rounded-sm border bg-paper text-center text-h2 font-semibold tabular-nums outline-none focus:border-ink focus:ring-2 focus:ring-ink/10 ${
                  error ? "border-danger" : d ? "border-ink" : "border-line"
                }`}
              />
            ))}
          </div>
          <div className="text-small text-muted" aria-live="polite">
            {seconds > 0 ? (
              <span>
                {resent ? "New code sent. " : ""}Resend code in <span className="tabular-nums">0:{String(seconds).padStart(2, "0")}</span>
              </span>
            ) : (
              <span className="flex flex-wrap gap-x-4 gap-y-1">
                <button type="button" onClick={() => resend()} className="font-medium text-sky-deep hover:underline">
                  Resend code
                </button>
                <button type="button" onClick={() => resend(data.otpChannel === "email" ? "whatsapp" : "email")} className="font-medium text-sky-deep hover:underline">
                  {data.otpChannel === "email" ? "Send it on WhatsApp instead" : "Email it to me instead"}
                </button>
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <GhostButton type="button" onClick={() => navigate(stepPath("tax"))}>
            <ArrowLeft size={18} strokeWidth={1.75} />
            Back
          </GhostButton>
          <PrimaryButton type="submit" loading={loading} loadingLabel="Verifying…" className="flex-1">
            Verify
          </PrimaryButton>
        </div>
      </form>
    </AuthLayout>
  );
}
